import React from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ProfessionalSummary = () => {
  const highlights = [
    {
      icon: "Code",
      title: "Full-Stack Development",
      description: "Expert in both frontend and backend technologies with 5+ years of hands-on experience"
    },
    {
      icon: "Zap",
      title: "Performance Optimization",
      description: "Specialized in building high-performance applications with 99% uptime and fast load times"
    },
    {
      icon: "Users",
      title: "Team Leadership",
      description: "Led development teams of 5-8 developers on multiple successful project deliveries"
    },
    {
      icon: "Lightbulb",
      title: "Problem Solving",
      description: "Strong analytical skills with a track record of solving complex technical challenges"
    }
  ];

  const experience = [
    {
      company: "",
      role: "Senior Full-Stack Developer",
      period: "2022 - Present",
      description: "Leading development of enterprise-scale applications serving 100K+ users"
    },
    {
      company: "",
      role: "Full-Stack Developer",
      period: "2020 - 2022",
      description: "Built MVP and scaled platform from 0 to 50K users in 18 months"
    },
    {
      company: "",
      role: "Frontend Developer",
      period: "2019 - 2020",
      description: "Developed responsive web applications for 20+ clients across various industries"
    }
  ];

  return (
    <section className="py-20 bg-surface">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Passionate Developer with a Vision
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                I'm Bello Fawas, a dedicated full-stack developer with over 5 years of experience creating innovative digital solutions. My passion lies in transforming complex problems into elegant, user-friendly applications that make a real impact.
              </p>
              <p className="text-muted-foreground">
                From startups to enterprise-level projects, I've helped businesses achieve their goals through clean code, scalable architecture, and cutting-edge technologies. I believe in continuous learning and staying ahead of industry trends to deliver exceptional results.
              </p>
            </div>

            {/* Highlights */}
            <div className="grid sm:grid-cols-2 gap-6">
              {highlights?.map((highlight, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Icon name={highlight?.icon} size={20} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">{highlight?.title}</h4>
                    <p className="text-sm text-muted-foreground">{highlight?.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/about-experience">
                <Button 
                  variant="default"
                  iconName="User"
                  iconPosition="left"
                  className="w-full sm:w-auto"
                >
                  Learn More About Me
                </Button>
              </Link>
              
              <Button 
                variant="outline"
                iconName="Download"
                iconPosition="left"
                className="w-full sm:w-auto"
              >
                Download Resume
              </Button>
            </div>
          </div>

          {/* Profile & Experience */}
          <div className="space-y-8">
            {/* Profile Card */}
            <div className="bg-card rounded-xl p-6 shadow-subtle">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 rounded-full overflow-hidden">
                  <Image
                    src="#"
                    alt="Bello Fawas - Full-Stack Developer"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground">Bello Fawas</h3>
                  <p className="text-muted-foreground">Senior Full-Stack Developer</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <Icon name="MapPin" size={14} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">LAGOS, NG </span>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center">
                  <div className="text-lg font-bold text-foreground">5+</div>
                  <div className="text-xs text-muted-foreground">Years</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-foreground">50+</div>
                  <div className="text-xs text-muted-foreground">Projects</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-foreground">30+</div>
                  <div className="text-xs text-muted-foreground">Clients</div>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex justify-center space-x-4">
                <Button variant="ghost" size="icon">
                  <Icon name="Github" size={20} />
                </Button>
                <Button variant="ghost" size="icon">
                  <Icon name="Linkedin" size={20} />
                </Button>
                <Button variant="ghost" size="icon">
                  <Icon name="Twitter" size={20} />
                </Button>
                <Button variant="ghost" size="icon">
                  <Icon name="Mail" size={20} />
                </Button>
              </div>
            </div>

            {/* Experience Timeline */}
            <div className="bg-card rounded-xl p-6 shadow-subtle">
              <h4 className="text-lg font-semibold text-foreground mb-6 flex items-center">
                <Icon name="Briefcase" size={20} className="mr-2" />
                Recent Experience
              </h4>
              
              <div className="space-y-6">
                {experience?.map((exp, index) => (
                  <div key={index} className="relative">
                    {index !== experience?.length - 1 && (
                      <div className="absolute left-2 top-8 w-0.5 h-12 bg-border"></div>
                    )}
                    <div className="flex items-start space-x-3">
                      <div className="w-4 h-4 bg-primary rounded-full flex-shrink-0 mt-1"></div>
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1">
                          <h5 className="font-medium text-foreground">{exp?.role}</h5>
                          <span className="text-xs text-muted-foreground">{exp?.period}</span>
                        </div>
                        <p className="text-sm text-primary font-medium mb-1">{exp?.company}</p>
                        <p className="text-xs text-muted-foreground">{exp?.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProfessionalSummary;